/**
 * Data Masking Utility for Claims Portal
 *
 * Masks PII (Personally Identifiable Information) in data before sending to DataDog
 * Adapted for Claims Portal data structures
 */

/**
 * Simple recursive masking function that masks PII fields in an object
 * @param data - The data object to mask
 * @param doMaskData - Whether to perform masking (false for development)
 * @returns Masked data object
 */
export function maskData(data: any, doMaskData: boolean = true): any {
  if (!doMaskData || !data) {
    return data;
  }

  // Clone the object to avoid mutating the original
  // Use try-catch to handle circular references
  let maskedData: any;
  try {
    maskedData = JSON.parse(JSON.stringify(data));
  } catch (err) {
    // If circular reference or other JSON error, return simplified version
    console.warn('[maskData] Failed to clone data, using shallow copy:', err);
    maskedData = { ...data };
  }

  // List of PII fields to mask (case-insensitive)
  const piiFieldNames = [
    'firstname',
    'lastname',
    'fullname',
    'name',
    'email',
    'phone',
    'phonenumber',
    'healthcard',
    'healthcardnumber',
    'accountnumber',
    'transitnumber',
    'institutionnumber',
    'birthdate',
    'dateofbirth',
    'address',
    'street',
    'city',
    'postalcode',
    'zipcode',
    'cardnumber',
    'cvv',
    'securitycode',
    'jwt',
    'token',
    'password',
    'contractnumber', // Keep for audit trail
  ];

  /**
   * Recursively mask fields in an object
   */
  const seen = new WeakSet(); // Track visited objects to prevent circular references

  function maskObject(obj: any, depth: number = 0): any {
    // Prevent infinite recursion
    if (depth > 10) {
      return '[Max Depth Reached]';
    }

    if (obj === null || obj === undefined) {
      return obj;
    }

    // Check for circular references
    if (typeof obj === 'object') {
      if (seen.has(obj)) {
        return '[Circular Reference]';
      }
      seen.add(obj);
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => maskObject(item, depth + 1));
    }

    if (typeof obj === 'object') {
      const masked: any = {};

      for (const [key, value] of Object.entries(obj)) {
        const lowerKey = key.toLowerCase();

        // Check if this key contains PII
        const isPII = piiFieldNames.some((field) =>
          lowerKey.includes(field.toLowerCase())
        );

        if (isPII && typeof value === 'string') {
          // Mask the value
          if (lowerKey.includes('email')) {
            // Mask email: keep first char and domain
            masked[key] = maskEmail(value);
          } else if (lowerKey.includes('phone')) {
            // Mask phone: keep area code
            masked[key] = maskPhone(value);
          } else if (lowerKey.includes('card') || lowerKey.includes('account')) {
            // Mask card/account: keep first and last digit
            masked[key] = maskCard(value);
          } else {
            // Mask other PII: keep first 2 chars
            masked[key] = maskString(value);
          }
        } else if (typeof value === 'object') {
          // Recursively mask nested objects
          masked[key] = maskObject(value, depth + 1);
        } else {
          // Keep non-PII values as-is
          masked[key] = value;
        }
      }

      return masked;
    }

    return obj;
  }

  return maskObject(maskedData);
}

/**
 * Mask email address: a***@example.com
 */
function maskEmail(email: string): string {
  if (!email || !email.includes('@')) return '***@***';

  const [localPart, domain] = email.split('@');
  const maskedLocal = localPart.charAt(0) + '***';
  return `${maskedLocal}@${domain}`;
}

/**
 * Mask phone number: (123) ***-****
 */
function maskPhone(phone: string): string {
  if (!phone) return '***-***-****';

  // Extract digits only
  const digits = phone.replace(/\D/g, '');

  if (digits.length < 4) return '***';

  // Keep area code (first 3 digits)
  const areaCode = digits.substring(0, 3);
  return `(${areaCode}) ***-****`;
}

/**
 * Mask card/account number: 1234********5678
 */
function maskCard(cardNumber: string): string {
  if (!cardNumber) return '****';

  // Extract digits only
  const digits = cardNumber.replace(/\D/g, '');

  if (digits.length < 4) return '****';

  // Keep first and last digit
  const first = digits.charAt(0);
  const last = digits.charAt(digits.length - 1);
  const masked = '*'.repeat(Math.max(digits.length - 2, 4));

  return `${first}${masked}${last}`;
}

/**
 * Mask general string: keep first 2 chars
 */
function maskString(value: string): string {
  if (!value) return '***';

  if (value.length <= 2) return '**';

  const visible = value.substring(0, 2);
  const masked = '*'.repeat(Math.max(value.length - 2, 3));

  return `${visible}${masked}`;
}
